import React, { useState, useEffect } from "react";
import sortBy from "lodash/sortBy";
import { useIntl } from "react-intl";
import { Spinner, Card, Row, Space, Badge } from "@masterteam/mt-design-react";

import Breadcrumb from "../Breadcrumb";
import ListsContainer from "../ListsContainer";
import DoughnutChart from "../DoughnutChart";

import api from "../../providers/api";
import { addToast } from "../../helpers";
import useIsAr from "../../Hooks/useIsAr";

import styles from "./ObjectiveDetails.module.scss";

function AttributeDetails({ match }) {
  const [objective, setObjective] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [kpisStatus, setKpisStatus] = useState([]);
  const [kpisTypes, setKpisTypes] = useState([]);
  const [initiativesStatuses, setInitiativesStatuses] = useState([]);
  const [isAr] = useIsAr();
  const intl = useIntl();

  async function getData() {
    setDataLoading(true);
    try {
      const [resStatuses, resTypes, dataRes, initiativesStatuses] =
        await Promise.all([
          api.handshake.getStatuses(),
          api.kpiType.getAll(),
          api.subSo.getByIdWithKPIs(match.params.id),
          api.initiatives.getStatuses(),
        ]);
      setKpisStatus(resStatuses.Data ? sortBy(resStatuses.Data, "Order") : []);
      setKpisTypes(resTypes.Data || []);
      setObjective(dataRes.Data);
      setInitiativesStatuses(initiativesStatuses.Data.Items || []);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => {
    getData();
  }, []);

  const pillarItem = objective?.StrategicObjective?.Perspective;

  const kpisList = objective?.KPIs;
  const initiativesList = objective?.Initiatives;

  return (
    <Space direction="vertical" size="large">
      {dataLoading ? (
        <div className="loder-container" style={{ marginTop: "32px" }}>
          <Spinner size="large" />
        </div>
      ) : (
        <>
          <Card
            className={styles.objectiveInfoContainer}
            style={{
              borderTopColor: objective?.StrategicObjective?.Perspective?.Color,
            }}
          >
            <Row justify="between">
              <div>
                <Badge
                  className={styles.perspectiveName}
                  backgroundColor={
                    objective?.StrategicObjective?.Perspective?.Color
                  }
                >
                  {isAr
                    ? objective?.StrategicObjective?.Perspective?.ArabicName
                    : objective?.StrategicObjective?.Perspective?.EnglishName}
                </Badge>

                <Space className={styles.infoWrapper} size="large">
                  <Card
                    className={styles.codeWrapper}
                    style={{
                      backgroundColor: kpisStatus.find(
                        (e) => objective.Status === e.Code
                      )?.Color,
                    }}
                  >
                    <div>{objective?.Code}</div>
                    <div className={styles.separator} />
                    <div>{objective?.Performance}%</div>
                  </Card>

                  <Space direction="vertical" className={styles.objectiveInfo}>
                    <p
                      className={styles.title}
                      style={{
                        color:
                          objective?.StrategicObjective?.Perspective?.Color,
                      }}
                    >
                      {isAr ? objective.ArabicName : objective.EnglishName}
                    </p>
                    <Breadcrumb
                      data={[
                        pillarItem?.Strategy,
                        objective?.StrategicObjective,
                        objective,
                      ]}
                    />
                  </Space>
                </Space>
              </div>
              <Space size="large" className={styles.charts}>
                <div className={styles.chartWrapper}>
                  <DoughnutChart
                    data={objective?.KPIDistribution || []}
                    statuses={kpisStatus || []}
                    count={kpisList?.length}
                    title={intl.formatMessage({ id: "common.kpis" })}
                  />
                </div>

                <div className={styles.line} />

                <div className={styles.chartWrapper}>
                  <DoughnutChart
                    data={objective?.InitiativeDistribution || []}
                    statuses={initiativesStatuses}
                    count={initiativesList?.length}
                    title={intl.formatMessage({
                      id: "common.initiatives",
                    })}
                  />
                </div>
              </Space>
            </Row>
          </Card>

          <ListsContainer
            kpiStatuses={kpisStatus}
            kpiTypes={kpisTypes}
            KPIs={kpisList}
            isAttributeDetails
            Initiatives={initiativesList}
          />
        </>
      )}
    </Space>
  );
}

export default AttributeDetails;
